import React, { useEffect, useState } from "react";
import { CURRENCY_CODE } from "../../config/constants";
import { Link } from "react-router-dom";
import { routes } from "../../config/utility";

export const PetSelection = ({ pets, state, dispatch, setCharges, permission }) => {
	const [extraPetCharge, setExtraPetCharge] = useState(0); // To store the extra charge for pets above the threshold
	const extraChargePerPet = permission.PAW_TAXI_EXTRA_PET_CHARGE; // Charge per extra pet
	const petThreshold = permission.PAW_TAXI_PET_THRESHOLD; // Maximum number of pets before extra charge applies

  // Toggle pet selection using dispatch to update the pet_ids field
  const togglePetSelection = (petId) => {
    const isSelected = state.pet_ids.includes(petId);
    let newPetIds;
    if (isSelected) {
      newPetIds = state.pet_ids.filter((id) => id !== petId);
    } else {
      newPetIds = [...state.pet_ids, petId];
    }
    dispatch({ type: "UPDATE_FIELD", field: "pet_ids", payload: newPetIds });
  };

  // Calculate extra charge when the pet_ids array changes
  useEffect(() => {
    const selectedPets = state.pet_ids.length;
    if (selectedPets > petThreshold) {
      const extraPets = selectedPets - petThreshold;
      const extraCharge = extraPets * extraChargePerPet;
      setExtraPetCharge(extraCharge);
      setCharges((prevData) => ({
        ...prevData,
        pet: extraCharge,
      }));
    } else {
      setExtraPetCharge(0);
      setCharges((prevData) => ({
        ...prevData,
        pet: 0,
      }));
    }
  }, [state.pet_ids]);

  return (
    <div className="mb-4 p-2 sm:p-4">
      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-purple-700 text-center mb-2 sm:mb-4">
	  Add Your Pets for Paw Taxi! 🚖🐾
      </h2>
      <p className="text-sm sm:text-base text-center text-gray-600 mb-4 sm:mb-6 px-2">
	  Select the pets you want us to safely transport with Paw Taxi! 🐕🚗

      </p>
      
      {/* Container with horizontal scroll on mobile */}
      <div className="relative w-full">
        <div className="flex sm:grid sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 overflow-x-auto sm:overflow-visible snap-x snap-mandatory scrollbar-none pb-6 sm:pb-0">
          {pets.length > 0 ? (
            pets.map((pet) => (
              <div
                key={pet.id}
                className="flex-none h-64 sm:h-fit sm:flex-auto w-[200px] sm:w-full snap-center first:ml-4 last:mr-4 sm:first:pl-0 sm:last:pr-0"
                onClick={() => togglePetSelection(pet.id)}
              >
                <div
                  className={`relative inline-block mt-8 sm:mt-0 h-48 w-48 sm:h-56 sm:w-56 md:h-64 md:w-64 rounded-full  overflow-hidden border-4 transition-all duration-300 ease-in-out transform hover:scale-105 ${
                    state.pet_ids.includes(pet.id)
                      ? "border-purple-600 scale-105 shadow-lg"
                      : "border-gray-300 hover:border-purple-300"
                  }`}
                >
                  <img
                    src={pet.primary_image_url}
                    alt={pet.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  {state.pet_ids.includes(pet.id) && (
                    <div className="absolute inset-0 bg-purple-600 bg-opacity-20 flex items-center justify-center">
                      <span className="bg-white rounded-full p-2 shadow-lg">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-sm sm:text-base font-medium text-gray-700 text-center">{pet.name}</p>
                {/* <p className="text-xs sm:text-sm text-gray-500 text-center">{pet.breed}</p> */}
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-6 sm:py-8">
              <p className="text-gray-600 mb-4 text-sm sm:text-base">You don't have any pets yet.</p>
              <Link
                to={routes.pet}
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
              >
                Create New Pet
              </Link>
            </div>
          )}
        </div>

        {/* Scroll Indicators - Only visible on mobile */}
        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 sm:hidden">
          {pets.map((_, index) => (
            <div
              key={index}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === 0 ? 'w-6 bg-purple-600' : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="mt-4 px-2">
        <p className="text-xs sm:text-sm text-center text-gray-500 italic">
          Note: Selecting more than {petThreshold} pets will incur an additional charge of {extraChargePerPet} {CURRENCY_CODE} per extra pet.
        </p>
      </div>

      {extraPetCharge > 0 && (
        <div className="mt-4 sm:mt-6 px-2">
          <div className="bg-purple-50 p-3 sm:p-4 rounded-lg border border-purple-100">
            <p className="text-base sm:text-lg font-semibold text-gray-800 text-center">
              Extra Charge for Additional Pets:{' '}
              <span className="text-purple-600 font-bold">
                {extraPetCharge + " " + CURRENCY_CODE}
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};